<template>
  <div id="linkedin_analytics_grid">
    <InfoGrid :columns="3">
      <ContainerInfoItem
        v-for="(data, key) in analyticsData"
        :key="key"
        source="sequence_linkedin_activity_analytics"
        :title="data.title"
        :description="range?.label || ''"
        :value="formatAnalyticsValue(data.value, data.unit)"
        :is-loading="data.isLoading"
      />
    </InfoGrid>
  </div>
</template>

<script setup>
import {computed, reactive, watch, watchEffect} from "vue";
import { formatAnalyticsValue } from "@/src/utils/analyticUtils.js";
import { sequenceLinkedinActions, sequenceActions, units } from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const baseMetrics = [
  {
    id: sequenceLinkedinActions.LINKEDIN_INVITE,
    title: "Connections sent",
    value: 0,
    isLoading: false,
  },
  {
    id: sequenceLinkedinActions.LINKEDIN_INVITE_ACCEPTED,
    title: "Connections accepted",
    value: 0,
    isLoading: false,
  },
  {
    id: sequenceLinkedinActions.LINKEDIN_INMAIL,
    title: "InMails Sent",
    value: 0,
    isLoading: false,
  },
  {
    id: sequenceLinkedinActions.LINKEDIN_MESSAGE,
    title: "Messages Sent",
    value: 0,
    isLoading: false,
  },
  {
    id: sequenceLinkedinActions.LINKEDIN_INMAIL_REPLIES,
    title: "InMail Replies",
    value: 0,
    isLoading: false,
  },
  {
    id: sequenceLinkedinActions.LINKEDIN_MESSAGE_REPLIES,
    title: "Message Replies",
    value: 0,
    isLoading: false,
  },
  {
    id: sequenceLinkedinActions.LINKEDIN_SKIPPED,
    title: "Skipped",
    value: 0,
    isLoading: false,
  },
];

const rateMetrics = [
  {
    id: sequenceActions.LINKEDIN_ACCEPTANCE_RATE,
    title: "Connection acceptance rate",
    value: 0,
    isLoading: false,
    unit: units.RATE,
  },
  {
    id: sequenceActions.LINKEDIN_REPLY_RATE,
    title: "Reply rate",
    value: 0,
    isLoading: false,
    unit: units.RATE,
  },
];

const analyticsData = reactive([
  ...baseMetrics,
  ...rateMetrics,
]);

const fetchAnalytics = async () => {
  analyticsData.forEach((item) => {
    item.isLoading = true;
  });

  try {
    const params = {
      user_id: userIds.value,
      team_id: teamIds.value,
      start_time: range.value?.start,
      end_time: range.value?.end,
      sequence_id: filters.value?.sequence?.id
    };

    AnalyticService.V2.fetchSequenceLinkedinCount(params).then((response) => {
      if (response?.success) {
        const data = response?.data?.results;
        analyticsData.forEach((item) => {
          item.value = data[item.id] || 0;
        });
      }
    }).catch((error) => {
      console.error("An error occurred while fetching LinkedIn analytics: ", error);
    });

    AnalyticService.V2.fetchSequenceRateMetrics(params).then((response) => {
      if (response && response.data) {
        const linkedinRates = response.data.linkedin_rates || {};
        analyticsData.forEach((item) => {
          if (linkedinRates[item.id] !== undefined) {
            item.value = linkedinRates[item.id];
          }
        });
      }
    });

  } catch (error) {
    console.error("An error occurred while fetching analytics: ", error);
  } finally {
    analyticsData.forEach((item) => {
      item.isLoading = false;
    });
  }
};

watch([range, filters, userIds, teamIds], () => {
  if (range.value || filters.value?.sequence?.id) {
    fetchAnalytics();
  }
}, { deep: true });

useAnalyticsRefresh(fetchAnalytics, [range, userIds, teamIds]);
</script>
